import { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";
import type { Features } from "../constants/features.constants";
import { FEATURES } from "../constants/features.constants";

type FeaturesRecord = Record<string, boolean>;

interface FeaturesContextValue {
  features: FeaturesRecord;
}

interface UseFeatures {
  FEATURES: typeof FEATURES;
  hasFeatures: (flags: Features[]) => boolean;
}

const FeaturesContext = createContext<FeaturesContextValue | undefined>(
  undefined
);

function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const { workspace } = useAuth();

  // Extract features from the current workspace
  const features: FeaturesRecord = workspace?.features || {};

  return (
    <FeaturesContext.Provider value={{ features }}>
      {children}
    </FeaturesContext.Provider>
  );
}

const useFeatures = (): UseFeatures => {
  const context = useContext(FeaturesContext);

  if (context === undefined) {
    throw new Error("useFeatures must be used within a FeaturesProvider");
  }

  return useMemo(
    () => ({
      /**
       * All feature flags
       * @returns All feature flags
       */
      FEATURES,
      /**
       * Check if ALL provided feature flags are enabled (AND logic)
       * @param flags - Array of at least one feature flag name to check
       * @returns true if all features are enabled, false otherwise
       */
      hasFeatures: (flags: Features[]): boolean => {
        if (flags.length === 0) {
          return false;
        }

        return flags.every((flag) => !!context.features[flag]);
      },
    }),
    [context.features]
  );
};

export { FeaturesProvider, useFeatures };
